import { useState } from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { format } from 'date-fns';
import type { Coupon } from '@/Coupons/types/coupons';
import type { Recipe } from '../types/recipes';
import { useCouponAuth } from '@/Coupons/hooks/use-coupon-auth';
import { useClipCoupon } from '@/Coupons/hooks/use-clip-coupon';
import { useClippedCoupons } from '@/Coupons/hooks/use-clipped-coupons';
import { ClipToastProvider, useClipToast } from '@/Coupons/context/ClipToastContext';
import { CouponAuthModal } from '@/Coupons/components/CouponAuthModal';
import { IconCheck, IconLoader4, IconPlus, IconSparkles, IconTag } from '@tabler/icons-react';

interface RecipeCouponsSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  recipe: Recipe;
  matchingCoupons: Coupon[];
}

const getImageUrl = (coupon: Coupon): string => {
  return coupon.imageUrl || '';
};

const formatDate = (date: string | undefined): string | null => {
  if (!date) return null;
  try {
    return format(new Date(date), 'MM/dd/yyyy');
  } catch {
    return null;
  }
};

interface CompactCouponCardProps {
  coupon: Coupon;
  isClipped: boolean;
  isLoading: boolean;
  onClip: () => void;
}

const CompactCouponCard = ({ coupon, isClipped, isLoading, onClip }: CompactCouponCardProps) => {
  const imageUrl = getImageUrl(coupon);
  const expirationDate = formatDate(coupon.expirationDate);

  return (
    <div className="flex gap-3 p-3 bg-white border rounded-lg">
      {/* Coupon Image */}
      <div className="flex-shrink-0 w-16 h-16 flex items-center justify-center bg-gray-50 rounded">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={coupon.title || 'Coupon'}
            className="w-full h-full object-contain"
            loading="lazy"
          />
        ) : (
          <IconSparkles className="h-6 w-6 text-primary/30" />
        )}
      </div>

      {/* Coupon Info */}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-red-500 line-clamp-1">
          {coupon.title}
        </p>
        {(coupon.brand || coupon.subtitle) && (
          <p className="text-xs font-semibold text-foreground line-clamp-1">
            {coupon.brand || coupon.subtitle}
          </p>
        )}
        {coupon.description && (
          <p className="text-xs text-muted-foreground line-clamp-1">
            {coupon.description}
          </p>
        )}
        {expirationDate && (
          <p className="text-[10px] text-muted-foreground mt-1">
            Expires {expirationDate}
          </p>
        )}
      </div>

      {/* Clip Button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onClip();
        }}
        disabled={isLoading}
        className={`flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center transition-colors ${
          isClipped
            ? 'bg-emerald-500 hover:bg-emerald-600'
            : 'bg-red-400 hover:bg-red-500'
        }`}
      >
        {isLoading ? (
          <IconLoader4 className="h-4 w-4 text-white animate-spin" />
        ) : isClipped ? (
          <IconCheck className="h-4 w-4 text-white" />
        ) : (
          <IconPlus className="h-4 w-4 text-white" />
        )}
      </button>
    </div>
  );
};

const RecipeCouponsSheetContent = ({
  open,
  onOpenChange,
  recipe,
  matchingCoupons,
}: RecipeCouponsSheetProps) => {
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [pendingCoupon, setPendingCoupon] = useState<Coupon | null>(null);
  const [clippingCouponId, setClippingCouponId] = useState<string | null>(null);

  const { isAuthenticated } = useCouponAuth();
  const { showClipToast } = useClipToast();
  const { data: clippedCoupons = [] } = useClippedCoupons();

  const clipCouponMutation = useClipCoupon({
    onSuccess: () => {
      showClipToast('success');
      setClippingCouponId(null);
      setPendingCoupon(null);
    },
    onError: (error) => {
      showClipToast('error', error.message || 'Unable to clip coupon.');
      setClippingCouponId(null);
      setPendingCoupon(null);
    },
  });

  const performClip = (coupon: Coupon) => {
    const offerId = coupon.offerId || coupon.id;
    if (offerId) {
      setClippingCouponId(offerId);
      clipCouponMutation.mutate(offerId);
    }
  };

  const handleClip = (coupon: Coupon) => {
    const couponId = coupon.offerId || coupon.id;
    const isClipped = clippedCoupons.some(
      (c) => (c.id || c.offerId) === couponId
    );

    if (isClipped) return;
    if (clippingCouponId === couponId) return;

    setPendingCoupon(coupon);

    if (isAuthenticated) {
      performClip(coupon);
    } else {
      setAuthModalOpen(true);
    }
  };

  const isCouponClipped = (coupon: Coupon) => {
    const couponId = coupon.offerId || coupon.id;
    return clippedCoupons.some((c) => (c.id || c.offerId) === couponId);
  };

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="left" className="flex flex-col p-0">
          <SheetHeader className="px-6 pt-6 pb-4 border-b">
            <SheetTitle className="flex items-center gap-2">
              <IconTag className="h-5 w-5 text-amber-500" />
              Available Coupons
            </SheetTitle>
            <SheetDescription className="line-clamp-1">
              Matching coupons for "{recipe.RecipeTitle}"
            </SheetDescription>
          </SheetHeader>

          <ScrollArea className="flex-1 px-4 py-4">
            <div className="space-y-3">
              {matchingCoupons.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No matching coupons found for this recipe.
                </div>
              ) : (
                <>
                  <Badge variant="secondary" className="mb-2">
                    {matchingCoupons.length} {matchingCoupons.length === 1 ? 'coupon' : 'coupons'} found
                  </Badge>
                  {matchingCoupons.map((coupon) => {
                    const couponId = coupon.offerId || coupon.id;
                    return (
                      <CompactCouponCard
                        key={couponId}
                        coupon={coupon}
                        isClipped={isCouponClipped(coupon)}
                        isLoading={clippingCouponId === couponId}
                        onClip={() => handleClip(coupon)}
                      />
                    );
                  })}
                </>
              )}
            </div>
          </ScrollArea>
        </SheetContent>
      </Sheet>

      <CouponAuthModal
        coupon={pendingCoupon}
        open={authModalOpen}
        onOpenChange={setAuthModalOpen}
      />
    </>
  );
};

export const RecipeCouponsSheet = (props: RecipeCouponsSheetProps) => {
  return (
    <ClipToastProvider>
      <RecipeCouponsSheetContent {...props} />
    </ClipToastProvider>
  );
};
